import React from 'react';
import { CheckCircle2, Globe, Award, Users, Wrench, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { useLanguage } from '../contexts/LanguageContext';

export const About = () => {
  const { t } = useLanguage();
  const features = [
    {
      icon: Globe,
      title: 'Європейське походження',
      description: 'Виробництво Plastic Technology, Литва. Понад 30 років досвіду у сфері пластикових виробів'
    },
    {
      icon: Award,
      title: 'Сертифікація ЄС',
      description: 'Продукція сертифікована за стандартами EN, CE. Відповідність міжнародним нормам якості'
    },
    {
      icon: Users,
      title: 'Індивідуальний підхід',
      description: 'Нестандартні рішення за кресленнями клієнта. Гнучкість у виборі конфігурації'
    },
    {
      icon: Wrench,
      title: 'Повний сервіс',
      description: 'Від консультації до монтажу та сервісного обслуговування. Технічна підтримка 24/7'
    }
  ];

  const values = [
    'Європейська якість за справедливою ціною',
    'Чесність та прозорість у відносинах з клієнтами',
    'Відповідальність за кожен реалізований проєкт',
    'Постійне вдосконалення сервісу',
    'Екологічна свідомість та турбота про довкілля',
    'Партнерство заради спільного успіху'
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-10 right-20 w-96 h-96 bg-[rgb(1,157,180)] opacity-5 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                {t('about.title')}
              </h1>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {t('about.desc1')}
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {t('about.desc2')}
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                {t('about.desc3')}
              </p>
            </div>
            <div className="relative">
              <img
                src="/images/pura-tanks-2.jpg"
                alt="PURA Ukraine"
                className="rounded-2xl shadow-2xl w-full h-[450px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('about.ourAdvantages')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('about.advantagesSubtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgb(1, 157, 180, 0.1)' }}>
                      <Icon className="h-8 w-8" style={{ color: 'rgb(1, 157, 180)' }} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-sm text-gray-600">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Adaptation Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Адаптація до українських умов
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Ми розуміємо, що українські реалії мають свою специфіку. Тому всі наші рішення адаптовані з урахуванням:
              </p>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="h-6 w-6 flex-shrink-0 mt-0.5" style={{ color: 'rgb(1, 157, 180)' }} />
                  <div>
                    <strong className="text-gray-900">Нестабільного енергопостачання</strong>
                    <p className="text-gray-600 text-sm mt-1">Системи працюють з мінімальним споживанням енергії або повністю автономно</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="h-6 w-6 flex-shrink-0 mt-0.5" style={{ color: 'rgb(1, 157, 180)' }} />
                  <div>
                    <strong className="text-gray-900">Різних типів ґрунтів</strong>
                    <p className="text-gray-600 text-sm mt-1">Рекомендації щодо встановлення для різних геологічних умов</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="h-6 w-6 flex-shrink-0 mt-0.5" style={{ color: 'rgb(1, 157, 180)' }} />
                  <div>
                    <strong className="text-gray-900">Сезонного проживання</strong>
                    <p className="text-gray-600 text-sm mt-1">Можливість консервації системи на зимовий період</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="h-6 w-6 flex-shrink-0 mt-0.5" style={{ color: 'rgb(1, 157, 180)' }} />
                  <div>
                    <strong className="text-gray-900">Вимог екологічних служб</strong>
                    <p className="text-gray-600 text-sm mt-1">Допомога в підготовці документації та отриманні дозволів</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="h-6 w-6 flex-shrink-0 mt-0.5" style={{ color: 'rgb(1, 157, 180)' }} />
                  <div>
                    <strong className="text-gray-900">Українського законодавства</strong>
                    <p className="text-gray-600 text-sm mt-1">Відповідність нормативам України та європейським стандартам</p>
                  </div>
                </li>
              </ul>
            </div>
            <div>
              <img
                src="/images/pura-installation.jpg"
                alt="Адаптація до українських умов"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Наші цінності
            </h2>
            <p className="text-lg text-gray-600">
              Принципи, якими ми керуємося у роботі
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {values.map((value, index) => (
              <div key={index} className="flex items-start space-x-3 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: 'rgb(1, 157, 180)' }} />
                <span className="text-gray-800 font-medium">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'rgb(1, 157, 180)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">30+</div>
              <div className="text-white/90">{t('about.yearsExperience')}</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">5</div>
              <div className="text-white/90">{t('about.yearsWarranty')}</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">50+</div>
              <div className="text-white/90">{t('about.yearsService')}</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">98%</div>
              <div className="text-white/90">{t('about.cleaningDegree')}</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};